import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  MessageCircle,
  Calendar,
  Phone,
  AtSign,
  ShieldBan,
  ShieldCheck,
  Trash2,
} from "lucide-react";
import { AppHeader } from "@/components/sartn/AppHeader";
import { CategoryBadge } from "@/components/sartn/CategoryBadge";
import { ErrorState } from "@/components/sartn/ErrorState";
import { LoadingSkeleton } from "@/components/sartn/LoadingSkeleton";
import { useContactStore } from "@/stores/contactStore";
import { cn } from "@/lib/cn";
import type { ContactCategory } from "@/components/sartn/CategoryBadge";

const CATEGORIES: ContactCategory[] = ["family", "friend", "business", "vip", "other"];

const PAGE_ENTER = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.25 },
};

interface ContactDetailProps {
  contactId: number;
}

export default function ContactDetail({ contactId }: ContactDetailProps) {
  const {
    selectedContact,
    isLoading,
    error,
    fetchContactDetail,
    updateContactCategory,
    toggleBlockContact,
    deleteContact,
    clearSelectedContact,
    clearError,
  } = useContactStore();

  const [showCategoryPicker, setShowCategoryPicker] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    fetchContactDetail(contactId);
    return () => clearSelectedContact();
  }, [contactId, fetchContactDetail, clearSelectedContact]);

  function navigateBack() {
    window.location.hash = "/contacts";
  }

  async function handleCategoryChange(category: ContactCategory) {
    setShowCategoryPicker(false);
    await updateContactCategory(contactId, category);
  }

  async function handleToggleBlock() {
    if (!selectedContact) return;
    await toggleBlockContact(contactId, !selectedContact.isBlocked);
  }

  async function handleDelete() {
    try {
      await deleteContact(contactId);
      navigateBack();
    } catch {
      // error already set in store
    }
  }

  if (isLoading) {
    return (
      <div className="flex flex-1 flex-col">
        <AppHeader title="Contact" onBack={navigateBack} />
        <LoadingSkeleton variant="card" count={3} className="px-4 pt-4" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-1 flex-col">
        <AppHeader title="Contact" onBack={navigateBack} />
        <ErrorState
          message={error}
          onRetry={() => {
            clearError();
            fetchContactDetail(contactId);
          }}
        />
      </div>
    );
  }

  if (!selectedContact) return null;

  const fullName = selectedContact.lastName
    ? `${selectedContact.firstName} ${selectedContact.lastName}`
    : selectedContact.firstName;

  const firstSeenFormatted = selectedContact.lastMessageAt
    ? new Date(selectedContact.lastMessageAt).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : "Unknown";

  return (
    <div className="flex flex-1 flex-col">
      <AppHeader title={fullName} onBack={navigateBack} />

      <motion.main
        initial={PAGE_ENTER.initial}
        animate={PAGE_ENTER.animate}
        transition={PAGE_ENTER.transition}
        className="flex-1 px-4 pb-20 pt-4"
      >
        <AvatarSection name={fullName} firstName={selectedContact.firstName} />

        <InfoSection
          username={selectedContact.username}
          phone={selectedContact.phone}
        />

        <div className="mb-6 flex items-center justify-center">
          <button
            type="button"
            onClick={() => setShowCategoryPicker(!showCategoryPicker)}
            className="transition-transform active:scale-[0.97]"
          >
            <CategoryBadge category={selectedContact.category} size="md" />
          </button>
        </div>

        {showCategoryPicker && (
          <CategoryPicker
            current={selectedContact.category}
            onSelect={handleCategoryChange}
            onClose={() => setShowCategoryPicker(false)}
          />
        )}

        <StatsRow
          messageCount={selectedContact.messageCount}
          firstSeen={firstSeenFormatted}
        />

        <ActionButtons
          isBlocked={selectedContact.isBlocked}
          showDeleteConfirm={showDeleteConfirm}
          onToggleBlock={handleToggleBlock}
          onDeleteTap={() => setShowDeleteConfirm(true)}
          onDeleteConfirm={handleDelete}
          onDeleteCancel={() => setShowDeleteConfirm(false)}
        />
      </motion.main>
    </div>
  );
}

function AvatarSection({ name, firstName }: { name: string; firstName: string }) {
  const colors = [
    "bg-blue-600", "bg-emerald-600", "bg-purple-600", "bg-amber-600",
    "bg-rose-600", "bg-cyan-600", "bg-indigo-600", "bg-teal-600",
  ];
  let hash = 0;
  for (const char of firstName) {
    hash = char.charCodeAt(0) + ((hash << 5) - hash);
  }
  const avatarColor = colors[Math.abs(hash) % colors.length];

  return (
    <div className="mb-4 flex flex-col items-center">
      <div
        className={cn(
          "flex h-20 w-20 items-center justify-center rounded-full",
          "font-headline text-2xl font-bold text-white",
          avatarColor,
        )}
      >
        {firstName.charAt(0).toUpperCase()}
      </div>
      <h2 className="mt-3 font-headline text-xl font-bold text-[var(--color-text)]">{name}</h2>
    </div>
  );
}

function InfoSection({
  username,
  phone,
}: {
  username: string | null;
  phone: string | null;
}) {
  if (!username && !phone) return null;

  return (
    <div className="mb-4 flex flex-col items-center gap-1">
      {username && (
        <div className="flex items-center gap-1.5 text-sm text-[var(--color-text-secondary)]">
          <AtSign className="h-3.5 w-3.5" />
          <span>{username}</span>
        </div>
      )}
      {phone && (
        <div className="flex items-center gap-1.5 text-sm text-[var(--color-text-secondary)]">
          <Phone className="h-3.5 w-3.5" />
          <span>{phone}</span>
        </div>
      )}
    </div>
  );
}

function CategoryPicker({
  current,
  onSelect,
  onClose,
}: {
  current: ContactCategory;
  onSelect: (c: ContactCategory) => void;
  onClose: () => void;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="mb-6 rounded-[var(--radius-lg)] bg-[var(--color-surface)] p-3 shadow-sm"
    >
      <p className="mb-2 text-xs font-medium text-[var(--color-text-muted)]">
        Change category
      </p>
      <div className="flex flex-wrap gap-2">
        {CATEGORIES.map((cat) => (
          <button
            key={cat}
            type="button"
            onClick={() => onSelect(cat)}
            className={cn(
              "transition-transform active:scale-[0.95]",
              cat === current && "ring-2 ring-[var(--color-primary)] ring-offset-1 ring-offset-[var(--color-surface)] rounded-[var(--radius-full)]",
            )}
          >
            <CategoryBadge category={cat} size="md" />
          </button>
        ))}
      </div>
      <button
        type="button"
        onClick={onClose}
        className="mt-2 text-xs text-[var(--color-text-muted)] transition-colors
                   hover:text-[var(--color-text-secondary)]"
      >
        Cancel
      </button>
    </motion.div>
  );
}

function StatsRow({
  messageCount,
  firstSeen,
}: {
  messageCount: number;
  firstSeen: string;
}) {
  return (
    <div className="mb-6 grid grid-cols-2 gap-3">
      <div className="flex flex-col items-center rounded-[var(--radius-lg)] bg-[var(--color-surface)] p-4">
        <MessageCircle className="mb-1 h-5 w-5 text-[var(--color-primary)]" />
        <span className="font-headline text-lg font-bold text-[var(--color-text)]">
          {messageCount}
        </span>
        <span className="text-xs text-[var(--color-text-muted)]">Messages</span>
      </div>
      <div className="flex flex-col items-center rounded-[var(--radius-lg)] bg-[var(--color-surface)] p-4">
        <Calendar className="mb-1 h-5 w-5 text-[var(--color-secondary)]" />
        <span className="text-sm font-bold text-[var(--color-text)]">
          {firstSeen}
        </span>
        <span className="text-xs text-[var(--color-text-muted)]">First seen</span>
      </div>
    </div>
  );
}

function ActionButtons({
  isBlocked,
  showDeleteConfirm,
  onToggleBlock,
  onDeleteTap,
  onDeleteConfirm,
  onDeleteCancel,
}: {
  isBlocked: boolean;
  showDeleteConfirm: boolean;
  onToggleBlock: () => void;
  onDeleteTap: () => void;
  onDeleteConfirm: () => void;
  onDeleteCancel: () => void;
}) {
  return (
    <div className="flex flex-col gap-3">
      <button
        type="button"
        onClick={onToggleBlock}
        className={cn(
          "flex items-center justify-center gap-2 rounded-[var(--radius-md)] py-3",
          "text-sm font-medium transition-colors active:scale-[0.98]",
          isBlocked
            ? "bg-green-50 text-green-700"
            : "bg-amber-50 text-amber-700",
        )}
      >
        {isBlocked ? (
          <>
            <ShieldCheck className="h-4 w-4" /> Unblock Contact
          </>
        ) : (
          <>
            <ShieldBan className="h-4 w-4" /> Block Contact
          </>
        )}
      </button>

      {!showDeleteConfirm ? (
        <button
          type="button"
          onClick={onDeleteTap}
          className="flex items-center justify-center gap-2 rounded-[var(--radius-md)]
                     bg-red-50 py-3 text-sm font-medium text-red-700
                     transition-colors active:scale-[0.98]"
        >
          <Trash2 className="h-4 w-4" /> Delete Contact
        </button>
      ) : (
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onDeleteCancel}
            className="flex-1 rounded-[var(--radius-md)] bg-[var(--color-surface)] py-3
                       text-sm font-medium text-[var(--color-text-secondary)]
                       transition-colors active:scale-[0.98]"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onDeleteConfirm}
            className="flex-1 rounded-[var(--radius-md)] bg-red-600 py-3
                       text-sm font-medium text-white
                       transition-colors active:scale-[0.98]"
          >
            Confirm Delete
          </button>
        </div>
      )}
    </div>
  );
}
